/* eslint-disable no-template-curly-in-string */

export const Category = {
    AGGREGATION: 'AGGREGATION',
    ALL: 'ALL',
    BITWISE: 'BITWISE',
    CONDITIONAL: 'CONDITIONAL',
    CONTEXT: 'CONTEXT',
    CONVERSION: 'CONVERSION',
    DATE: 'DATE',
    NUMERIC: 'NUMERIC',
    STRING: 'STRING',
    WINDOWING: 'WINDOWING',
    STRUCTURED: 'STRUCTURED'
};

export const functions = [
    {
        name: 'ABS',
        category: Category.NUMERIC,
        syntax: 'ABS(<expr>)',
        completion: 'ABS(${expr})',
        summary: 'Returns the absolute value of a numeric expression.'
    },
    {
        name: 'ACOS',
        category: Category.NUMERIC,
        syntax: 'ACOS(<expr>)',
        completion: 'ACOS(${expr})',
        summary:
            'Computes the inverse cosine (arc cosine) of its input; the result is a number in the interval [0, pi].'
    },
    {
        name: 'ACOSH',
        category: Category.NUMERIC,
        syntax: 'ACOSH(<expr>)',
        completion: 'ACOSH(${expr})',
        summary: 'Computes the inverse (arc) hyperbolic cosine of its input.'
    },
    {
        name: 'ADD_MONTHS',
        category: Category.DATE,
        syntax: 'ADD_MONTHS(<date_or_time_expr>, <num_months_expr>)',
        completion: 'ADD_MONTHS(${date_or_time_expr}, ${num_months_expr})',
        summary:
            'Adds or subtracts a specified number of months to a date or timestamp, preserving the end-of-month information.'
    },
    {
        name: 'ASCII',
        category: Category.NUMERIC,
        syntax: 'ASCII(<expr>)',
        completion: 'ASCII(${expr})',
        summary:
            'Returns the ASCII code for the first character of a string. If the string is empty, a value of 0 is returned.'
    },
    {
        name: 'ASIN',
        category: Category.NUMERIC,
        syntax: 'ASIN(<expr>)',
        completion: 'ASIN(${expr})',
        summary:
            'Computes the inverse sine (arc sine) of its argument; the result is a number in the interval [-pi/2, pi/2].'
    },
    {
        name: 'ASINH',
        category: Category.NUMERIC,
        syntax: 'ASINH(<expr>)',
        completion: 'ASINH(${expr})',
        summary: 'Computes the inverse (arc) hyperbolic sine of its argument.'
    },
    {
        name: 'ATAN',
        category: Category.NUMERIC,
        syntax: 'ATAN(<expr>)',
        completion: 'ATAN(${expr})',
        summary:
            'Computes the inverse tangent (arc tangent) of its argument; the result is a number in the interval [-pi, pi].'
    },
    {
        name: 'ATAN2',
        category: Category.NUMERIC,
        syntax: 'ATAN2(<y> , <x>)',
        completion: 'ATAN2(${y}, ${x})',
        summary: 'Computes the inverse tangent (arc tangent) of the ratio of its two arguments'
    },
    {
        name: 'ATANH',
        category: Category.NUMERIC,
        syntax: 'ATANH(<expr>)',
        completion: 'ATANH(${expr})',
        summary: 'Computes the inverse (arc) hyperbolic tangent of its argument.'
    },
    {
        name: 'BITAND',
        category: Category.BITWISE,
        syntax: 'BITAND(<expr1>, <expr2>)',
        completion: 'BITAND(${expr1}, ${expr2})',
        summary: 'Returns the bitwise AND of two numeric expressions.'
    },
    {
        name: 'BITNOT',
        category: Category.BITWISE,
        syntax: 'BITNOT(<expr>)',
        completion: 'BITNOT(${expr})',
        summary: 'Returns the bitwise negation of a numeric expression.'
    },
    {
        name: 'BITOR',
        category: Category.BITWISE,
        syntax: 'BITOR(<expr1>, <expr2>)',
        completion: 'BITOR(${expr1}, ${expr2})',
        summary: 'Returns the bitwise OR of two numeric expressions.'
    },
    {
        name: 'BITSHIFTLEFT',
        category: Category.BITWISE,
        syntax: 'BITSHIFTLEFT(<expr>, <n>)',
        completion: 'BITSHIFTLEFT(${expr}, ${n})',
        summary: 'Shifts the bits for a numeric expression n positions to the left.'
    },
    {
        name: 'BITSHIFTRIGHT',
        category: Category.BITWISE,
        syntax: 'BITSHIFTRIGHT(<expr>, <n>)',
        completion: 'BITSHIFTRIGHT(${expr}, ${n})',
        summary: 'Shifts the bits for a numeric expression n positions to the right.'
    },
    {
        name: 'BIT_LENGTH',
        category: Category.STRING,
        syntax: 'BIT_LENGTH(<string_or_binary>)',
        completion: 'BIT_LENGTH(${string_or_binary})',
        summary: 'Returns the length of a string or binary value in bits.'
    },
    {
        name: 'BIXOR',
        category: Category.BITWISE,
        syntax: 'BITXOR(<expr1>, <expr2>)',
        completion: 'BITXOR(${expr1}, ${expr2})',
        summary: 'Returns the bitwise XOR of two numeric expressions.'
    },
    {
        name: 'BOOLAND',
        category: Category.CONDITIONAL,
        syntax: 'BOOLAND(<expr1>, <expr2>)',
        completion: 'BOOLAND(${expr1}, ${expr2})',
        summary: 'Computes the Boolean AND of two numeric expressions.'
    },
    {
        name: 'BOOLNOT',
        category: Category.CONDITIONAL,
        syntax: 'BOOLNOT(<expr>)',
        completion: 'BOOLNOT(${expr})',
        summary: 'Computes the Boolean NOT of a single numeric expression.'
    },
    {
        name: 'BOOLOR',
        category: Category.CONDITIONAL,
        syntax: 'BOOLOR(<expr1>, <expr2>)',
        completion: 'BOOLOR(${expr1}, ${expr2})',
        summary: 'Computes the Boolean OR of two numeric expressions.'
    },
    {
        name: 'BOOLXOR',
        category: Category.CONDITIONAL,
        syntax: 'BOOLXOR(<expr1>, <expr2>)',
        completion: 'BOOLXOR(${expr1}, ${expr2})',
        summary: 'Computes the Boolean XOR of two numeric expressions.'
    },
    {
        name: 'CASE',
        category: Category.CONDITIONAL,
        syntax: 'CASE WHEN <condition1> THEN <result1> [...] [ELSE <result3>] END',
        completion: 'CASE WHEN${condition} THEN${result1} ELSE${result2} END',
        summary: 'Evaluates a list of conditions until one is TRUE and returns the result.'
    },
    {
        name: 'CAST',
        category: Category.CONVERSION,
        syntax: 'CAST(<source_expr> AS <target_data_type>)',
        completion: 'CAST(${source_expr} AS ${target_data_type})',
        summary: 'Converts a value of one data type into another data type.'
    },
    {
        name: 'CBRT',
        category: Category.NUMERIC,
        syntax: 'CBRT(<expr>)',
        completion: 'CBRT(${expr})',
        summary: 'Returns the cubic root of a numeric expression.'
    },
    {
        name: 'CEIL',
        category: Category.NUMERIC,
        syntax: 'CEIL(<expr>)',
        completion: 'CEIL(${expr})',
        summary: 'Returns values from <expr> rounded to the nearest equal or larger integer.'
    },
    {
        name: 'CHAR',
        category: Category.STRING,
        syntax: 'CHAR(<expr>)',
        completion: 'CHAR(${expr})',
        summary:
            'Converts a Unicode code point (including 7-bit ASCII) into the character that matches the input Unicode. If an invalid code point is specified, an error is returned.'
    },
    {
        name: 'CHARINDEX',
        category: Category.STRING,
        syntax: 'CHARINDEX( <expr1>, <expr2> [ , <start_pos> ] )',
        completion: 'CHARINDEX(${expr1} IN${expr2})',
        summary:
            'Searches for the first occurrence of the first argument in the second argument and, if successful, returns the position (1-based) of the first argument in the second argument.'
    },
    {
        name: 'COALESCE',
        category: Category.CONDITIONAL,
        syntax: 'COALESCE(<expr1>, <expr2> [, ..., <exprN> ])',
        completion: 'COALESCE(${expr1}, ${expr2})',
        summary:
            'Returns the first non-NULL expression among its arguments, or NULL if all its arguments are NULL.'
    },
    {
        name: 'CONCAT',
        category: Category.STRING,
        syntax: 'CONCAT(<expr1> [, <exprN> ...])',
        completion: 'CONCAT(${expr1}, ${expr2})',
        summary:
            'Concatenates one or more strings, or concatenates one or more binary values. If any of the values is null, the result is also null.'
    },
    {
        name: 'CONCAT_WS',
        category: Category.STRING,
        syntax: 'CONCAT_WS(<separator>, <expr1> [, <exprN> ...])',
        completion: 'CONCAT_WS(${separator}, ${expr1}, ${expr2})',
        summary:
            'Concatenates two or more strings, or concatenates two or more binary values. If any of the values is null, the result is also null.'
    },
    {
        name: 'CONVERT_TIMEZONE',
        category: Category.DATE,
        syntax: 'CONVERT_TIMEZONE(<target_tz> , <source_timestamp>)',
        completion: 'CONVERT_TIMEZONE(${target_tz}, ${source_timestamp})',
        summary: 'Converts a timestamp to another time zone.'
    },
    {
        name: 'CONTAINS',
        category: 'STRING',
        syntax: 'CONTAINS(<expr1>,<expr2>)',
        completion: 'CONTAINS(${expr1}, ${expr2})',
        summary:
            'Returns true if the first parameter contains the second one. Both expressions must be text or binary expressions.'
    },
    {
        name: 'COS',
        category: Category.NUMERIC,
        syntax: 'COS(<expr>)',
        completion: 'COS(${expr})',
        summary: 'Computes the cosine of its argument; the argument should be expressed in radians.'
    },
    {
        name: 'COT',
        category: Category.NUMERIC,
        syntax: 'COT(<expr>)',
        completion: 'COT(${expr})',
        summary:
            'Computes the cotangent of its argument; the argument should be expressed in radians.'
    },
    {
        name: 'CURRENT_DATE',
        category: Category.CONTEXT,
        syntax: 'CURRENT_DATE()',
        completion: 'CURRENT_DATE()',
        summary: 'Returns the current date.'
    },
    {
        name: 'CURRENT_TIME',
        category: Category.CONTEXT,
        syntax: 'CURRENT_TIME()',
        completion: 'CURRENT_TIME()',
        summary: 'Returns the current time.'
    },
    {
        name: 'CURRENT_TIMESTAMP',
        category: Category.CONTEXT,
        syntax: 'CURRENT_TIMESTAMP()',
        completion: 'CURRENT_TIMESTAMP()',
        summary: 'Returns the current date and time.'
    },
    {
        name: 'DATEADD',
        category: Category.DATE,
        syntax: 'DATEADD(<date_or_time_part>, <value>, <date_or_time_expr>)',
        completion: 'DATEADD(${date_or_time_part}, ${value}, ${date_or_time_expr})',
        summary:
            'Adds the specified value for the specified date or time part to a date, time, or timestamp.'
    },
    {
        name: 'DATEDIFF',
        category: Category.DATE,
        syntax: 'DATEDIFF(<date_or_time_part>, <date_or_time_expr1>, <date_or_time_expr2>)',
        completion: 'DATEDIFF(${date_or_time_part}, ${date_or_time_expr1}, ${date_or_time_expr2})',
        summary:
            'Calculates the difference between two date, time, or timestamp expressions based on the date or time part requested. The function returns the result of subtracting the second argument from the third argument.'
    },
    {
        name: 'DATE_PART',
        category: Category.DATE,
        syntax: 'DATE_PART(<date_or_time_part>, <date_or_time_expr>)',
        completion: 'DATE_PART(${date_or_time_part}, ${date_or_time_expr})',
        summary: 'Extracts the specified date or time part from a date, time, or timestamp.'
    },
    {
        name: 'DATE_TRUNC',
        category: Category.DATE,
        syntax: 'DATE_TRUNC( <part>, <date_or_time_expr> )',
        completion: 'DATE_TRUNC(${part}, ${date_or_time_expr})',
        summary: 'Truncates a DATE, TIME, or TIMESTAMP to the specified precision.'
    },
    {
        name: 'DATE_FROM_PARTS',
        category: Category.DATE,
        syntax: 'DATE_FROM_PARTS(<year>, <month>, <day>)',
        completion: 'DATE_FROM_PARTS(${year}, ${month}, ${day})',
        summary:
            'Creates a date from individual numeric components that represent the year, month, and day of the month.'
    },
    {
        name: 'DAY',
        category: Category.DATE,
        syntax: 'DAY(<date_or_timestamp_expr>)',
        completion: 'DAY(${time_expr})',
        summary: 'Extracts the day from a date or timestamp.'
    },
    {
        name: 'DAYNAME',
        category: Category.DATE,
        syntax: 'DAYNAME(<date_or_timestamp_expr>)',
        completion: 'DAYNAME(${date_or_timestamp_expr})',
        summary: 'Extracts the three-letter day-of-week name from the specified date or timestamp.'
    },
    {
        name: 'DAYOFWEEK',
        category: Category.DATE,
        syntax: 'DAYOFWEEK(<date_or_timestamp_expr>)',
        completion: 'DAYOFWEEK(${time_expr})',
        summary: 'Extracts the day of the week from a date or timestamp.'
    },
    {
        name: 'DAYOFMONTH',
        category: Category.DATE,
        syntax: 'DAYOFMONTH(<date_or_timestamp_expr>)',
        completion: 'DAYOFMONTH(${time_expr})',
        summary: 'Extracts the day of the month from a date or timestamp.'
    },
    {
        name: 'DAYOFYEAR',
        category: Category.DATE,
        syntax: 'DAYOFYEAR(<date_or_timestamp_expr>)',
        completion: 'DAYOFYEAR(${time_expr})',
        summary: 'Extracts the day of the year from a date or timestamp.'
    },
    {
        name: 'DECODE',
        category: Category.CONDITIONAL,
        syntax: 'DECODE(<expr>, <search1>, <result1> [, <search2>, <result2>, ...] [, <default>])',
        completion: 'DECODE(${expr}, ${search1}, ${result1})',
        summary:
            'Compares the select expression to each search expression in order. As soon as a search expression matches the selection expression, the corresponding result expression is returned.'
    },
    {
        name: 'DEGREES',
        category: Category.NUMERIC,
        syntax: 'DEGREES(<expr>)',
        completion: 'DEGREES(${expr})',
        summary: 'Converts radians to degrees.'
    },
    {
        name: 'DIV0',
        category: Category.NUMERIC,
        syntax: 'DIV0(<dividend>, <divisor>)',
        completion: 'DIV0(${dividend}, ${divisor})',
        summary:
            'Performs division like the division operator (/), but returns 0 when the divisor is 0 (rather than reporting an error).'
    },
    {
        name: 'DIV0NULL',
        category: Category.NUMERIC,
        syntax: 'DIV0NULL(<dividend>, <divisor>)',
        completion: 'DIV0NULL(${dividend}, ${divisor})',
        summary:
            'Performs division like the division operator (/), but returns 0 when the divisor is 0 or NULL (rather than reporting an error or returning NULL).'
    },
    {
        name: 'EDITDISTANCE',
        category: Category.STRING,
        syntax: 'EDITDISTANCE( <string_expr1> , <string_expr2> [, <max_distance> ] )',
        completion: 'EDITDISTANCE(${string_expr1}, ${string_expr2})',
        summary:
            'Computes the Levenshtein distance between two input strings. It is the number of single-character insertions, deletions, or substitutions needed to convert one string to another.'
    },
    {
        name: 'EXP',
        category: Category.NUMERIC,
        syntax: 'EXP(<expr>)',
        completion: 'EXP(${expr})',
        summary:
            'Computes Euler’s number e (approximately 2.718281) raised to a floating-point value <expr>.'
    },
    {
        name: 'ENDSWITH',
        category: Category.STRING,
        syntax: 'ENDSWITH(<expr1>, <expr2>)',
        completion: 'ENDSWITH(${expr1}, ${expr2})',
        summary:
            'Returns TRUE if the first expression ends with second expression. Both expressions must be text or binary expressions.'
    },
    {
        name: 'EQUAL_NULL',
        category: Category.CONDITIONAL,
        syntax: 'EQUAL_NULL(<expr1>, <expr2>)',
        completion: 'EQUAL_NULL(${expr1}, ${expr2})',
        summary:
            'Compares whether two expressions are equal. The function is NULL-safe, meaning it treats NULLs as known values for comparing equality.'
    },
    {
        name: 'EXTRACT',
        category: Category.DATE,
        syntax: 'EXTRACT(<date_or_time_part> FROM <date_or_time_expr>)',
        completion: 'EXTRACT(${date_or_time_part} FROM${date_or_time_expr})',
        summary: 'Extracts the specified date or time part from a date, time, or timestamp.'
    },
    {
        name: 'FACTORIAL',
        category: Category.NUMERIC,
        syntax: 'FACTORIAL(<integer_expr>)',
        completion: 'FACTORIAL(${integer_expr})',
        summary:
            'Computes the factorial of its input. The input argument must be an integer expression in the range of 0 to 33.'
    },
    {
        name: 'FLOOR',
        category: Category.NUMERIC,
        syntax: 'FLOOR(<expr>)',
        completion: 'FLOOR(${expr})',
        summary: 'Returns values from <expr> rounded to the nearest equal or smaller integer.'
    },
    {
        name: 'GETBIT',
        category: Category.BITWISE,
        syntax: 'GETBIT(<expr>)',
        completion: 'GETBIT(${expr})',
        summary: 'Given an INTEGER value, returns the value of a bit at a specified position.'
    },
    {
        name: 'GREATEST',
        category: Category.NUMERIC,
        syntax: 'GREATEST(<expr1> [, <expr2> ...])',
        completion: 'GREATEST(${expr1}, ${expr2})',
        summary:
            'Returns the largest value from a list of expressions. If any of the argument values is NULL, the result is NULL.'
    },
    {
        name: 'HASH',
        category: Category.STRING,
        syntax: 'HASH( <expr> [ , <expr2> ... ] )',
        completion: 'HASH(${expr})',
        summary:
            'Returns a signed 64-bit hash value. Note that HASH never returns NULL, even for NULL inputs.'
    },
    {
        name: 'HOUR',
        category: Category.DATE,
        syntax: 'HOUR(<time_or_timestamp_expr>)',
        completion: 'HOUR(${expr})',
        summary: 'Extracts the hour from a time or timestamp value.'
    },
    {
        name: 'IFF',
        category: Category.CONDITIONAL,
        syntax: 'IFF(<condition>, <expr1>, <expr2>)',
        completion: 'IFF(${condition}, ${expr1}, ${expr2})',
        summary: 'If condition evaluates to TRUE, returns expr1, otherwise returns expr2.'
    },
    {
        name: 'IFNULL',
        category: Category.CONDITIONAL,
        syntax: 'IFNULL(<expr1>, <expr2>)',
        completion: 'IFNULL(${expr1}, ${expr2})',
        summary: 'If expr1 is NULL, returns expr2, otherwise returns expr1.'
    },
    {
        name: 'INITCAP',
        category: Category.STRING,
        syntax: 'INITCAP(<expr1>)',
        completion: 'INITCAP(${expr1})',
        summary:
            'Returns the input string (expr) with the first letter of each word in uppercase and the subsequent letters in lowercase.'
    },
    {
        name: 'INSERT',
        category: Category.STRING,
        syntax: 'INSERT( <base_expr>, <pos>, <len>, <insert_expr> )',
        completion: 'INSERT(${base_expr}, ${pos}, ${len}, ${insert_expr})',
        summary:
            'Replaces a substring of the specified length, starting at the specified position, with a new string or binary value.'
    },
    {
        name: 'IS_NULL_VALUE',
        category: Category.CONDITIONAL,
        syntax: 'IS_NULL_VALUE(<variant_expr>)',
        completion: 'IS_NULL_VALUE(${variant_expr})',
        summary: 'Returns true if its VARIANT argument is a JSON null value.'
    },
    {
        name: 'LAST_DAY',
        category: Category.DATE,
        syntax: 'LAST_DAY(<date_or_timestamp_expr>, [ , <date_part> ])',
        completion: 'LAST_DAY(${date_or_timestamp_expr})',
        summary:
            'Returns the last day of the specified date part for a date or timestamp. Commonly used to return the last day of the month for a date or timestamp.'
    },
    {
        name: 'LEAST',
        category: Category.NUMERIC,
        syntax: 'LEAST(<expr1> [, <expr2> ...])',
        completion: 'LEAST(${expr1}, ${expr2})',
        summary:
            'Returns the smallest value from a list of expressions. If any of the argument values is NULL, the result is NULL.'
    },
    {
        name: 'LEFT',
        category: Category.STRING,
        syntax: 'LEFT(<string_expr>, <length_expr>)',
        completion: 'LEFT(${string_expr}, ${length_expr})',
        summary:
            'Returns a leftmost substring of its input. LEFT(STR,N) is equivalent to SUBSTR(STR,1,N).'
    },
    {
        name: 'LENGTH',
        category: Category.STRING,
        syntax: 'LENGTH(<string_or_binary>)',
        completion: 'LENGTH(${string_or_binary})',
        summary: 'Returns the length of an input string or binary value.'
    },
    {
        name: 'LN',
        category: Category.NUMERIC,
        syntax: 'LN(<expr>)',
        completion: 'LN(${expr})',
        summary: 'Returns the natural logarithm of a numeric expression.'
    },
    {
        name: 'LOG',
        category: Category.NUMERIC,
        syntax: 'LOG(<base>, <expr>)',
        completion: 'LOG(${base}, ${expr})',
        summary: 'Returns the logarithm of a numeric expression.'
    },
    {
        name: 'LOWER',
        category: Category.STRING,
        syntax: 'LOWER(<expr>)',
        completion: 'LOWER(${expr})',
        summary: 'Returns the input string (<expr>) with all characters converted to lowercase.'
    },
    {
        name: 'LPAD',
        category: Category.STRING,
        syntax: 'LPAD(<base>, <length_expr> [, <pad>])',
        completion: 'LPAD(${base}, ${length}, ${pad})',
        summary:
            'The argument (base) is left-padded to length length_expr with characters/bytes from the pad argument.'
    },
    {
        name: 'LTRIM',
        category: Category.STRING,
        syntax: 'LTRIM(<expr> [, <characters>])',
        completion: 'LTRIM(${expr}, ${characters})',
        summary: 'Removes leading characters, including whitespace, from a string.'
    },
    {
        name: 'MINUTE',
        category: Category.DATE,
        syntax: 'MINUTE(<time_or_timestamp_expr>)',
        completion: 'MINUTE(${expr})',
        summary: 'Extracts the minute from a time or timestamp value.'
    },
    {
        name: 'MOD',
        category: Category.NUMERIC,
        syntax: 'MOD(<expr1>, <expr2>)',
        completion: 'MOD(${expr1}, ${expr2})',
        summary: 'Returns the remainder of input <expr1> divided by input <expr2>.'
    },
    {
        name: 'MONTH',
        category: Category.DATE,
        syntax: 'MONTH(<date_or_timestamp_expr>)',
        completion: 'MONTH(${time_expr})',
        summary: 'Extracts the month from a date or timestamp.'
    },
    {
        name: 'MONTHNAME',
        category: Category.DATE,
        syntax: 'MONTHNAME(<date_or_time_expr>)',
        completion: 'MONTHNAME(${expr1})',
        summary: 'Extracts the three-letter month name from the specified date or timestamp.'
    },
    {
        name: 'MONTHS_BETWEEN',
        category: Category.DATE,
        syntax: 'MONTHS_BETWEEN(<date_expr1> , <date_expr2>)',
        completion: 'MONTHS_BETWEEN(${date_expr1}, ${date_expr2})',
        summary: 'Returns the number of months between two DATE or TIMESTAMP values.'
    },
    {
        name: 'NEXT_DAY',
        category: Category.DATE,
        syntax: 'NEXT_DAY(<date_or_time_expr>, <dow_string>)',
        completion: 'NEXT_DAY(${expr}, ${dow})',
        summary:
            'Returns the date of the first specified DOW (day of week) that occurs after the input date.'
    },
    {
        name: 'NORMAL',
        category: Category.NUMERIC,
        syntax: 'NORMAL(<mean> , <stddev> , <gen>)',
        completion: 'NORMAL(${mean}, ${stddev}, ${gen})',
        summary:
            'Generates a normally-distributed pseudo-random floating point number with specified mean and stddev (standard deviation).'
    },
    {
        name: 'NULLIF',
        category: Category.CONDITIONAL,
        syntax: 'NULLIF(<expr1>, <expr2>)',
        completion: 'NULLIF(${expr1}, ${expr2})',
        summary: 'Returns NULL if expr1 is equal to expr2, otherwise returns expr1.'
    },
    {
        name: 'NULLIFZERO',
        category: Category.CONDITIONAL,
        syntax: 'NULLIFZERO(<expr>)',
        completion: 'NULLIFZERO(${expr})',
        summary: 'Returns NULL if the argument evaluates to 0; otherwise, returns the argument.'
    },
    {
        name: 'NVL',
        category: Category.CONDITIONAL,
        syntax: 'NVL(<expr1>, <expr2>)',
        completion: 'NVL(${expr1}, ${expr2})',
        summary: 'If expr1 is NULL, returns expr2, otherwise returns expr1.'
    },
    {
        name: 'NVL2',
        category: Category.CONDITIONAL,
        syntax: 'NVL(<expr1>, <expr2>, <expr3>)',
        completion: 'NVL2(${expr1}, ${expr2}, ${expr3})',
        summary: 'Returns values depending on whether the first input is NULL'
    },
    {
        name: 'OCTET_LENGTH',
        category: Category.STRING,
        syntax: 'OCTET_LENGTH(<string_or_binary>)',
        completion: 'OCTET_LENGTH(${string_or_binary})',
        summary: 'Returns the length of a string or binary value in bytes.'
    },
    {
        name: 'PARSE_IP',
        category: Category.STRING,
        syntax: "PARSE_IP(<expr>, '< type >' [, <permissive>])",
        completion: 'PARSE_IP(${expr}, ${type})',
        summary:
            'Returns a JSON object consisting of all the components from a valid INET (Internet Protocol) or CIDR (Classless Internet Domain Routing) IPv4 or IPv6 string.'
    },
    {
        name: 'PARSE_URL',
        category: Category.STRING,
        syntax: 'PARSE_URL(<string>, [<permissive>])',
        completion: 'PARSE_URL(${expr})',
        summary:
            'Returns a JSON object consisting of all the components (fragment, host, path, port, query, scheme) in a valid input URL/URI.'
    },
    {
        name: 'PI',
        category: Category.NUMERIC,
        syntax: 'PI()',
        completion: 'PI()',
        summary: 'Returns the value of pi as a floating-point value.'
    },
    {
        name: 'POSITION',
        category: Category.STRING,
        syntax: 'POSITION(<expr1> IN <expr2>)',
        completion: 'POSITION(${expr1} IN${expr2})',
        summary:
            'Searches for the first occurrence of the first argument in the second argument and, if successful, returns the position (1-based) of the first argument in the second argument.'
    },
    {
        name: 'POWER',
        category: Category.NUMERIC,
        syntax: 'POWER(<x>, <y>)',
        completion: 'POWER(${x}, ${y})',
        summary: 'Returns a number (<x>) raised to the specified power (<y>).'
    },
    {
        name: 'PREVIOUS_DAY',
        category: Category.DATE,
        syntax: 'PREVIOUS_DAY(<date_or_time_expr>, <dow_string>)',
        completion: 'PREVIOUS_DAY(${expr}, ${dow})',
        summary:
            'Returns the date of the first specified DOW (day of week) that occurs before the input date.'
    },
    {
        name: 'QUARTER',
        category: Category.DATE,
        syntax: 'QUARTER(<date_or_timestamp_expr>)',
        completion: 'QUARTER(${time_expr})',
        summary: 'Extracts the quarter from a date or timestamp.'
    },
    {
        name: 'RADIANS',
        category: Category.NUMERIC,
        syntax: 'RADIANS(<real_expr>)',
        completion: 'RADIANS(${expr})',
        summary: 'Converts degrees to radians.'
    },
    {
        name: 'RANDOM',
        category: Category.NUMERIC,
        syntax: 'RANDOM(<seed>)',
        completion: 'RANDOM(${expr})',
        summary: 'Returns a pseudo-random 64-bit integer.'
    },
    {
        name: 'RANDSTR',
        category: Category.STRING,
        syntax: 'RANDSTR(<length>, <seed>)',
        completion: 'RANDSTR(${length}, ${expr})',
        summary: 'Returns a random string of specified length.'
    },
    {
        name: 'REGEXP',
        category: Category.STRING,
        syntax: '<subject> REGEXP <pattern>',
        completion: '${subject} REGEXP${pattern}',
        summary:
            'Returns true if the subject matches the specified pattern. Both inputs must be text expressions.'
    },
    {
        name: 'REGEXP_COUNT',
        category: Category.STRING,
        syntax: 'REGEXP_COUNT(<subject>, <pattern> [, <position>, <parameters>])',
        completion: 'REGEXP_COUNT(${subject}, ${pattern})',
        summary: 'Returns the number of times that a pattern occurs in a string.'
    },
    {
        name: 'REGEXP_INSTR',
        category: Category.STRING,
        syntax: 'REGEXP_INSTR(<subject>, <pattern> [, <position> [, <occurrence> [, <option> [, <regexp_parameters> [, <group_num>]]]]])',
        completion: 'REGEXP_INSTR(${subject}, ${pattern})',
        summary:
            'Returns the position of the specified occurrence of the regular expression pattern in the string subject. If no match is found, returns 0.'
    },
    {
        name: 'REGEXP_LIKE',
        category: Category.STRING,
        syntax: 'REGEXP_LIKE(<subject>, <pattern> [, <parameters>])',
        completion: 'REGEXP_LIKE(${subject}, ${pattern})',
        summary:
            'Returns true if the subject matches the pattern. Both expressions must be text expressions.'
    },
    {
        name: 'REGEXP_REPLACE',
        category: Category.STRING,
        syntax: 'REGEXP_REPLACE(<subject>, <pattern> [, <replacement>, <position>, <occurrence>, <parameters>])',
        completion: 'REGEXP_REPLACE(${subject}, ${pattern})',
        summary:
            'Returns the subject with the specified pattern (or all occurrences of the pattern) either removed or replaced by a replacement string. If no matches are found, returns the original subject.'
    },
    {
        name: 'REGEXP_SUBSTR',
        category: Category.STRING,
        syntax: 'REGEXP_SUBSTR(<subject>, <pattern> [, <position> [, <occurrence> [, <regex_parameters> [, <group_num>]]]])',
        completion: 'REGEXP_SUBSTR(${subject}, ${pattern})',
        summary:
            'Returns the substring that matches a regular expression within a string. If no match is found, returns NULL.'
    },
    {
        name: 'REGEXP_SUBSTR_ALL',
        category: Category.STRING,
        syntax: 'REGEXP_SUBSTR_ALL( <subject> , <pattern> [ , <position> [ , <occurrence> [ , <regex_parameters> [ , <group_num> ] ] ] ] )',
        completion: 'REGEXP_SUBSTR_ALL(${subject}, ${pattern})',
        summary:
            'Returns an ARRAY that contains all substrings that match a regular expression within a string. If no match is found, the function returns an empty ARRAY.'
    },
    {
        name: 'REGR_VALX',
        category: Category.CONDITIONAL,
        syntax: 'REGR_VALX(<y> , <x>)',
        completion: 'REGR_VALX(${y}, ${x})',
        summary:
            'Returns NULL if the first argument is NULL; otherwise, returns the second argument.'
    },
    {
        name: 'REGR_VALY',
        category: Category.CONDITIONAL,
        syntax: 'REGR_VALY(<y> , <x>)',
        completion: 'REGR_VALY(${y}, ${x})',
        summary:
            'Returns NULL if the second argument is NULL; otherwise, returns the first argument.'
    },
    {
        name: 'REPEAT',
        category: Category.STRING,
        syntax: 'REPEAT(<input>, <n>)',
        completion: 'REPEAT(${expr}, ${n})',
        summary: 'Builds a string by repeating the input for the specified number of times.'
    },
    {
        name: 'REPLACE',
        category: Category.STRING,
        syntax: 'REPLACE(<subject>, <pattern> [, <replacement>])',
        completion: 'REPLACE(${subject}, ${pattern})',
        summary:
            'Removes all occurrences of a specified substring, and optionally replaces them with another string.'
    },
    {
        name: 'REVERSE',
        category: Category.STRING,
        syntax: 'REVERSE(<subject>)',
        completion: 'REVERSE(${subject})',
        summary: 'Reverses the order of characters in a string, or of bytes in a binary value.'
    },
    {
        name: 'RIGHT',
        category: Category.STRING,
        syntax: 'RIGHT(<expr>, <length_expr>)',
        completion: 'RIGHT(${expr}, ${length})',
        summary: 'Returns a rightmost substring of its input.'
    },
    {
        name: 'ROUND',
        category: Category.NUMERIC,
        syntax: 'ROUND(<input_expr> [, <scale_expr>])',
        completion: 'ROUND(${expr})',
        summary: 'Returns rounded values for input_expr.'
    },
    {
        name: 'RPAD',
        category: Category.STRING,
        syntax: 'RPAD(<base>, <length_expr> [, <pad>])',
        completion: 'RPAD(${expr}, ${length}, ${pad})',
        summary:
            'Right-pads a string with characters from another string, or right-pads a binary value with bytes from another binary value.'
    },
    {
        name: 'RTRIM',
        category: Category.STRING,
        syntax: 'RTRIM(<expr> [, <characters>])',
        completion: 'RTRIM(${expr})',
        summary: 'Removes trailing characters, including whitespace, from a string.'
    },
    {
        name: 'RTRIMMED_LENGTH',
        category: Category.STRING,
        syntax: 'RTRIMMED_LENGTH( <string_expr> )',
        completion: 'RTRIMMED_LENGTH(${string_expr})',
        summary:
            'Returns the length of its argument, minus trailing whitespace, but including leading whitespace.'
    },
    {
        name: 'SEQ1',
        category: Category.NUMERIC,
        syntax: 'SEQ1([0|1])',
        completion: 'SEQ1()',
        summary: 'Returns a sequence of monotonically increasing integers, with wrap-around.'
    },
    {
        name: 'SEQ2',
        category: Category.NUMERIC,
        syntax: 'SEQ2([0|1])',
        completion: 'SEQ2()',
        summary: 'Returns a sequence of monotonically increasing integers, with wrap-around.'
    },
    {
        name: 'SEQ4',
        category: Category.NUMERIC,
        syntax: 'SEQ4([0|1])',
        completion: 'SEQ4()',
        summary: 'Returns a sequence of monotonically increasing integers, with wrap-around.'
    },
    {
        name: 'SEQ8',
        category: Category.NUMERIC,
        syntax: 'SEQ8([0|1])',
        completion: 'SEQ8()',
        summary: 'Returns a sequence of monotonically increasing integers, with wrap-around.'
    },
    {
        name: 'SIGN',
        category: Category.NUMERIC,
        syntax: 'SIGN(<expr>)',
        completion: 'SIGN(${expr})',
        summary: 'Returns the sign of its argument:'
    },
    {
        name: 'SIN',
        category: Category.NUMERIC,
        syntax: 'SIN(<expr>)',
        completion: 'SIN(${expr})',
        summary: 'Computes the sine of its argument; the argument should be expressed in radians.'
    },
    {
        name: 'SINH',
        category: Category.NUMERIC,
        syntax: 'SINH(<expr>)',
        completion: 'SINH(${expr})',
        summary: 'Computes the hyperbolic sine of its argument.'
    },
    {
        name: 'SECOND',
        category: Category.DATE,
        syntax: 'SECOND(<time_or_timestamp_expr>)',
        completion: 'SECOND(${expr})',
        summary: 'Extracts the second from a time or timestamp value.'
    },
    {
        name: 'SOUNDEX',
        category: Category.STRING,
        syntax: 'SOUNDEX(<varchar_expr>)',
        completion: 'SOUNDEX(${expr})',
        summary: 'Returns a string that contains a phonetic representation of the input string.'
    },
    {
        name: 'SOUNDEX_P123',
        category: Category.STRING,
        syntax: 'SOUNDEX_P123(<varchar_expr>)',
        completion: 'SOUNDEX_P123(${expr})',
        summary:
            'Returns a string that contains a phonetic representation of the input string, and retains the Soundex code number for the second letter when the first and second letters use the same number.'
    },
    {
        name: 'SPACE',
        category: Category.STRING,
        syntax: 'SPACE(<n>)',
        completion: 'SPACE(${n})',
        summary: 'Builds a string consisting of the specified number of blank spaces.'
    },
    {
        name: 'SPLIT',
        category: Category.STRING,
        syntax: 'SPLIT(<string>, <separator>)',
        completion: 'SPLIT(${string}, ${separator})',
        summary:
            'Splits a given string with a given separator and returns the result in an array of strings.'
    },
    {
        name: 'SPLIT_PART',
        category: Category.STRING,
        syntax: 'SPLIT_PART(<string>, <delimiter>, <partNumber>)',
        completion: 'SPLIT_PART(${string}, ${delimiter}m${partNumber})',
        summary: 'Splits a given string at a specified character and returns the requested part.'
    },
    {
        name: 'SQRT',
        category: Category.NUMERIC,
        syntax: 'SQRT(<expr>)',
        completion: 'SQRT(${expr})',
        summary: 'Returns the square-root of a non-negative numeric expression.'
    },
    {
        name: 'SQUARE',
        category: Category.NUMERIC,
        syntax: 'SQUARE(<expr>)',
        completion: 'SQUARE(${expr})',
        summary:
            'Returns the square of a numeric expression (i.e. a numeric expression multiplied by itself).'
    },
    {
        name: 'STARTSWITH',
        category: Category.STRING,
        syntax: 'STARTSWITH(<expr1>, <expr2>)',
        completion: 'STARTSWITH(${expr1}, ${expr2})',
        summary:
            'Returns true if expr1 starts with expr2. Both expressions must be text or binary expressions.'
    },
    {
        name: 'STRTOK',
        category: Category.STRING,
        syntax: 'STRTOK(<string> [,<delimiter>] [,<partNr>])',
        completion: 'STRTOK(${string}, ${delimiter}, ${part})',
        summary: 'Tokenizes a given string and returns the requested part.'
    },
    {
        name: 'STRTOK_TO_ARRAY',
        category: Category.STRING,
        syntax: 'STRTOK_TO_ARRAY(<string> [,<delimiter>])',
        completion: 'STRTOK_TO_ARRAY(${string}, ${delimiter})',
        summary:
            'Tokenizes the given string using the given set of delimiters and returns the tokens as an array.'
    },
    {
        name: 'SUBSTRING',
        category: Category.STRING,
        syntax: 'SUBSTRING(<base_expr>, <start_expr> [, <length_expr>])',
        completion: 'SUBSTRING(${expr}, ${start}, ${length})',
        summary:
            'Returns the portion of the string or binary value from <base_expr>, starting from the character/byte specified by <start_expr>, with optionally limited <length>.'
    },
    {
        name: 'TAN',
        category: Category.NUMERIC,
        syntax: 'TAN(<real_expr>)',
        completion: 'TAN(${expr})',
        summary:
            'Computes the tangent of its argument; the argument should be expressed in radians.'
    },
    {
        name: 'TANH',
        category: Category.NUMERIC,
        syntax: 'TANH(<real_expr>)',
        completion: 'TANH(${expr})',
        summary: 'Computes the hyperbolic tangent of its argument.'
    },
    {
        name: 'TIME_FROM_PARTS',
        category: Category.DATE,
        syntax: 'TIME_FROM_PARTS(<hour>, <minute>, <second> [, <nanoseconds>])',
        completion: 'TIME_FROM_PARTS(${hour}, ${minute}, ${second})',
        summary: 'Creates a time from individual numeric components.'
    },
    {
        name: 'TIMESLICE',
        category: Category.DATE,
        syntax: 'TIMESLICE(<date_or_time_expr>, <slice_length>, <date_or_time_part> [ , <start_or_end> ])',
        completion: 'TIMESLICE(${date_or_time_expr}, ${slice_length}, ${date_or_time_part})',
        summary:
            'Calculates the beginning or end of a “slice” of time, where the length of the slice is a multiple of a standard unit of time (minute, hour, day, etc.).'
    },
    {
        name: 'TIMESTAMP_FROM_PARTS',
        category: Category.DATE,
        syntax: 'TIMESTAMP_FROM_PARTS(<year>, <month>, <day>, <hour>, <minute>, <second> [, <nanosecond> ] [, <time_zone> ])',
        completion:
            'TIMESTAMP_FROM_PARTS(${year}, ${month}, ${day}, ${hour}, ${minute}, ${second})',
        summary:
            'Creates a timestamp from individual numeric components. If no time zone is in effect, the function can be used to create a timestamp from a date expression and a time expression.'
    },
    {
        name: 'TO_ARRAY',
        category: Category.CONVERSION,
        syntax: 'TO_ARRAY(<expr>)',
        completion: 'TO_ARRAY(${expr})',
        summary: 'Converts an input expression to an array.'
    },
    {
        name: 'TO_BOOLEAN',
        category: Category.CONVERSION,
        syntax: 'TO_BOOLEAN(<text_or_numeric_expr>)',
        completion: 'TO_BOOLEAN(${text_or_numeric_expr})',
        summary:
            'Coverts the input text or numeric expression to a Boolean value. For NULL input, the output is NULL.'
    },
    {
        name: 'TO_BINARY',
        category: Category.CONVERSION,
        syntax: "TO_BINARY(<string_expr> [, '< format >'])",
        completion: 'TO_BINARY(${text_or_numeric_expr})',
        summary:
            'Converts the input expression to a binary value. For NULL input, the output is NULL.'
    },
    {
        name: 'TO_CHAR',
        category: Category.CONVERSION,
        syntax: 'TO_CHAR(<expr>)',
        completion: 'TO_CHAR(${expr})',
        summary: 'Converts the input expression to a string. For NULL input, the output is NULL.'
    },
    {
        name: 'TO_DECIMAL',
        category: Category.CONVERSION,
        syntax: "TO_DECIMAL(<expr>  [, '< format >' ] [, <precision> [, <scale> ] ])",
        completion: 'TO_DECIMAL(${expr})',
        summary:
            'Converts an input expression to a fixed-point number. For NULL input, the output is NULL.'
    },
    {
        name: 'TO_DATE',
        category: Category.DATE,
        syntax: 'TO_DATE(<string_expr> [, <format>])',
        completion: 'TO_DATE(${string_expr})',
        summary: 'Converts an input expression to a date.'
    },
    {
        name: 'TO_DOUBLE',
        category: Category.CONVERSION,
        syntax: "TO_DOUBLE(<expr>  [, '< format >' ])",
        completion: 'TO_DOUBLE(${expr})',
        summary:
            'Converts an expression to a double-precision floating-point number. For NULL input, the output is NULL.'
    },
    {
        name: 'TO_OBJECT',
        category: Category.CONVERSION,
        syntax: 'TO_OBJECT( <expr> )',
        completion: 'TO_OBJECT(${expr})',
        summary: 'Converts the input value to an OBJECT.'
    },
    {
        name: 'TO_TIME',
        category: Category.DATE,
        syntax: 'TO_TIME(<string_expr> [, <format>])',
        completion: 'TO_TIME(${string_expr})',
        summary: 'Converts an input expression into a time.'
    },
    {
        name: 'TO_TIMESTAMP',
        category: Category.DATE,
        syntax: 'TO_TIMESTAMP(<string_expr> [, <format>])',
        completion: 'TO_TIMESTAMP(${string_expr})',
        summary: 'Converts an input expression into the corresponding timestamp.'
    },
    {
        name: 'TO_TIMESTAMP_LTZ',
        category: Category.DATE,
        syntax: 'TO_TIMESTAMP_LTZ(<string_expr> [, <format>])',
        completion: 'TO_TIMESTAMP_LTZ(${string_expr})',
        summary:
            'Converts an input expression into the corresponding timestamp with local time zone.'
    },
    {
        name: 'TO_TIMESTAMP_NTZ',
        category: Category.DATE,
        syntax: 'TO_TIMESTAMP_NTZ(<string_expr> [, <format>])',
        completion: 'TO_TIMESTAMP_NTZ(${string_expr})',
        summary: 'Converts an input expression into the corresponding timestamp with no time zone.'
    },
    {
        name: 'TO_TIMESTAMP_TZ',
        category: Category.DATE,
        syntax: 'TO_TIMESTAMP_TZ(<string_expr> [, <format>])',
        completion: 'TO_TIMESTAMP_TZ(${string_expr})',
        summary: 'Converts an input expression into the corresponding timestamp with time zone.'
    },
    {
        name: 'TO_VARIANT',
        category: Category.CONVERSION,
        syntax: 'TO_VARIANT( <expr> )',
        completion: 'TO_VARIANT(${expr})',
        summary: 'Converts any value to a VARIANT value or NULL (if input is NULL).'
    },
    {
        name: 'TRANSLATE',
        category: Category.STRING,
        syntax: 'TRANSLATE(<subject>, <sourceAlphabet>, <targetAlphabet>)',
        completion: 'TRANSLATE(${subject}, ${sourceAlphabet}, ${targetAlphabet})',
        summary:
            'Translates <subject> from the characters in <sourceAlphabet> to the characters in <targetAlphabet>.'
    },
    {
        name: 'TRIM',
        category: Category.STRING,
        syntax: 'TRIM(<expr>)',
        completion: 'TRIM(${expr})',
        summary: 'Removes leading and trailing spaces from a string.'
    },
    {
        name: 'TRUNC',
        category: Category.DATE,
        syntax: 'TRUNC( <date_or_time_expr>, <part> )',
        completion: 'TRUNC(${date_or_time_expr}, ${part})',
        summary: 'Truncates a DATE, TIME, or TIMESTAMP to the specified part.'
    },
    {
        name: 'TRUNCATE',
        category: Category.NUMERIC,
        syntax: 'TRUNCATE(<input_expr> [, <scale>])',
        completion: 'TRUNCATE(${expr})',
        summary:
            'Rounds the input expression down to the nearest (or equal) integer closer to zero, or to the nearest equal or smaller value with the specified number of places after the decimal point.'
    },
    {
        name: 'TRY_CAST',
        category: Category.CONVERSION,
        syntax: 'TRY_CAST(<string_expr> AS <target_data_type>)',
        completion: 'TRY_CAST(${string_expr} AS ${target_data_type})',
        summary:
            'Converts a String value into another data type, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_PARSE_JSON',
        category: Category.CONVERSION,
        syntax: 'TRY_PARSE_JSON(<string_expr>)',
        completion: 'TRY_PARSE_JSON(${string_expr})',
        summary:
            'Parses an input String as a JSON document, producing a VARIANT value. Results in NULL if there is an error during parsing.'
    },
    {
        name: 'TRY_TO_BINARY',
        category: Category.CONVERSION,
        syntax: "TRY_TO_BINARY(<string_expr> [, '< format >'])",
        completion: 'TRY_TO_BINARY(${text_or_numeric_expr})',
        summary:
            'A special version of TO_BINARY that performs the same operation (i.e. converts an input expression to a binary value), but with error handling support (i.e. if the conversion cannot be performed, it returns a NULL value instead of raising an error).'
    },
    {
        name: 'TRY_TO_BOOLEAN',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_BOOLEAN(<string_expr>)',
        completion: 'TRY_TO_BOOLEAN(${string_expr})',
        summary:
            'Converts a String value into Boolean value, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_DATE',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_DATE(<string_expr> [, <format>])',
        completion: 'TRY_TO_DATE(${string_expr})',
        summary:
            'Converts a String value into Date, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_DECIMAL',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_DECIMAL(<string_expr> [, <format> ] [, <precision> [, <scale> ]])',
        completion: 'TRY_TO_DECIMAL(${string_expr})',
        summary:
            'Converts a String value into fixed-point number value, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_DOUBLE',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_DOUBLE(<string_expr>)',
        completion: 'TRY_TO_DOUBLE(${string_expr})',
        summary:
            'Converts a String value into double-precision floating-point value, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_NUMBER',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_NUMBER(<string_expr> [, <format> ] [, <precision> [, <scale> ]])',
        completion: 'TRY_TO_NUMBER(${string_expr})',
        summary:
            'Converts a String value into fixed-point number value, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_NUMERIC',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_NUMERIC(<string_expr> [, <format> ] [, <precision> [, <scale> ]])',
        completion: 'TRY_TO_NUMERIC(${string_expr})',
        summary:
            'Converts a String value into fixed-point number value, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_TIME',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_TIME(<string_expr> [, <format>])',
        completion: 'TRY_TO_TIME(${string_expr})',
        summary:
            'Converts a String value into Time, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_TIMESTAMP',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_TIMESTAMP(<string_expr> [, <format>])',
        completion: 'TRY_TO_TIMESTAMP(${string_expr})',
        summary:
            'Converts a String value into Timestamp, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_TIMESTAMP_LTZ',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_TIMESTAMP_LTZ(<string_expr> [, <format>])',
        completion: 'TRY_TO_TIMESTAMP_LTZ(${string_expr})',
        summary:
            'Converts a String value into Timestamp, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_TIMESTAMP_NTZ',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_TIMESTAMP_NTZ(<string_expr> [, <format>])',
        completion: 'TRY_TO_TIMESTAMP_NTZ(${string_expr})',
        summary:
            'Converts a String value into Timestamp, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'TRY_TO_TIMESTAMP_TZ',
        category: Category.CONVERSION,
        syntax: 'TRY_TO_TIMESTAMP_TZ(<string_expr> [, <format>])',
        completion: 'TRY_TO_TIMESTAMP_TZ(${string_expr})',
        summary:
            'Converts a String value into Timestamp, but returns a NULL value instead of raising an error when the conversion can not be performed.'
    },
    {
        name: 'UNICODE',
        category: Category.NUMERIC,
        syntax: 'UNICODE(<expr>)',
        completion: 'UNICODE(${expr})',
        summary:
            'Returns the Unicode code point for the first Unicode character in a string. If the string is empty, a value of 0 is returned.'
    },
    {
        name: 'UNIFORM',
        category: Category.NUMERIC,
        syntax: 'UNIFORM(<min>, <max>, <gen>)',
        completion: 'UNIFORM(${min}, ${max}, ${gen})',
        summary:
            'Generates a uniformly-distributed pseudo-random number in the inclusive range [min, max].'
    },
    {
        name: 'UPPER',
        category: Category.STRING,
        syntax: 'UPPER(<expr>)',
        completion: 'UPPER(${expr})',
        summary: 'Returns the input string <expr> with all characters converted to uppercase.'
    },
    {
        name: 'UUID_STRING',
        category: Category.STRING,
        syntax: 'UUID_STRING()',
        completion: 'UUID_STRING()',
        summary:
            'Generates either a version 4 (random) or version 5 (named) RFC 4122-compliant UUID as a formatted string.'
    },
    {
        name: 'WEEK',
        category: Category.DATE,
        syntax: 'WEEK(<date_or_timestamp_expr>)',
        completion: 'WEEK(${time_expr})',
        summary: 'Extracts the week from a date or timestamp.'
    },
    {
        name: 'WHEN',
        category: Category.CONDITIONAL,
        syntax: 'WHEN <condition> THEN <result>',
        completion: 'WHEN${condition} THEN${result}',
        summary: ''
    },
    {
        name: 'WIDTH_BUCKET',
        category: Category.NUMERIC,
        syntax: 'WIDTH_BUCKET( <expr> , <min_value> , <max_value> , <num_buckets> )',
        completion: 'WIDTH_BUCKET(${expr}, ${min_value}, ${max_value}, ${num_buckets})',
        summary:
            'Constructs equi-width histograms, in which the histogram range is divided into intervals of identical size, and returns the bucket number into which the value of an expression falls, after it has been evaluated. The function returns an integer value or null (if any input is null).'
    },
    {
        name: 'YEAR',
        category: Category.DATE,
        syntax: 'YEAR(<date_or_timestamp_expr>)',
        completion: 'YEAR(${time_expr})',
        summary: 'Extracts the year from a date or timestamp.'
    },
    {
        name: 'ZEROIFNULL',
        category: Category.CONDITIONAL,
        syntax: 'ZEROIFNULL(<expr>)',
        completion: 'ZEROIFNULL(${expr})',
        summary: 'Returns 0 if its argument is null; otherwise, returns its argument.'
    },
    {
        name: 'ZIPF',
        category: Category.NUMERIC,
        syntax: 'ZIPF(<s>, <N>, <gen>)',
        completion: 'ZIPF(${min}, ${max}, ${gen})',
        summary: 'Returns a Zipf-distributed integer, for N elements and characteristic exponent s.'
    },
    {
        name: 'ARRAY_APPEND',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_APPEND( <array> , <new_element> )',
        completion: 'ARRAY_APPEND(${array}, ${new_element})',
        summary:
            'Returns an array containing all elements from the source array as well as the new element.'
    },
    {
        name: 'ARRAY_CONTAINS',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_CONTAINS( <value_expr> , <array> )',
        completion: 'ARRAY_CONTAINS(${value_expr}, ${array})',
        summary: 'Returns TRUE if the specified value is found in the specified ARRAY.'
    },
    {
        name: 'AS_TIMESTAMP_TZ',
        category: Category.STRUCTURED,
        syntax: 'AS_TIMESTAMP_TZ( <variant_expr> )',
        completion: 'AS_TIMESTAMP_TZ(${variant_expr})',
        summary: 'Casts a VARIANT value to the respective TIMESTAMP value.'
    },
    {
        name: 'AS_DOUBLE',
        category: Category.STRUCTURED,
        syntax: 'AS_DOUBLE( <variant> )',
        completion: 'AS_DOUBLE(${variant})',
        summary: 'Casts a VARIANT value to a floating-point value.'
    },
    {
        name: 'AS_DATE',
        category: Category.STRUCTURED,
        syntax: 'AS_DATE( <variant_expr> )',
        completion: 'AS_DATE(${variant_expr})',
        summary: 'Casts a VARIANT value to a date.'
    },
    {
        name: 'IS_BINARY',
        category: Category.STRUCTURED,
        syntax: 'IS_BINARY( <variant_expr> )',
        completion: 'IS_BINARY(${variant_expr})',
        summary: 'Returns TRUE if its VARIANT argument contains a binary string.'
    },
    {
        name: 'ARRAY_TO_STRING',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_TO_STRING( <array> , <separator_string> )',
        completion: 'ARRAY_TO_STRING(${array}, ${separator_string})',
        summary:
            'Returns an input array converted to a string by casting all values to strings (using TO_VARCHAR) and concatenating them (using the string from the second argument to separate the elements).'
    },
    {
        name: 'AS_BOOLEAN',
        category: Category.STRUCTURED,
        syntax: 'AS_BOOLEAN( <variant> )',
        completion: 'AS_BOOLEAN(${variant})',
        summary: 'Casts a VARIANT value to a BOOLEAN value.'
    },
    {
        name: 'ARRAY_PREPEND',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_PREPEND( <array> , <new_element> )',
        completion: 'ARRAY_PREPEND(${array}, ${new_element})',
        summary:
            'Returns an array containing the new element as well as all elements from the source array.'
    },
    {
        name: 'MAP_SIZE',
        category: Category.STRUCTURED,
        syntax: 'MAP_SIZE( <map> )',
        completion: 'MAP_SIZE(${map})',
        summary: 'Returns the size of a MAP.'
    },
    {
        name: 'MAP_CAT',
        category: Category.STRUCTURED,
        syntax: 'MAP_CAT( <map1> , <map2> )',
        completion: 'MAP_CAT(${map1}, ${map2})',
        summary: 'Returns the concatenatation of two MAPs.'
    },
    {
        name: 'MAP_KEYS',
        category: Category.STRUCTURED,
        syntax: 'MAP_KEYS( <map> )',
        completion: 'MAP_KEYS(${map})',
        summary: 'Returns the keys in a MAP.'
    },
    {
        name: 'ARRAY_CONSTRUCT_COMPACT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_CONSTRUCT_COMPACT( [ <expr1> ] [ , <expr2> [ , ... ] ] )',
        completion: 'ARRAY_CONSTRUCT_COMPACT(${expr1}, ${expr2})',
        summary:
            'Returns an array constructed from zero, one, or more inputs; the constructed array omits any NULL input values.'
    },
    {
        name: 'MAP_DELETE',
        category: Category.STRUCTURED,
        syntax: 'MAP_DELETE( <map>, <key1> [, <key2>, ... ] )',
        completion: 'MAP_DELETE(${map}, ${key1})',
        summary: 'Returns a MAP based on an existing MAP with one or more keys removed..'
    },
    {
        name: 'ARRAY_MAX',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_MAX( <array> )',
        completion: 'ARRAY_MAX(${array})',
        summary:
            'Given an input ARRAY, returns the element with the highest value that is not a SQL NULL.'
    },
    {
        name: 'ARRAYS_TO_OBJECT',
        category: Category.STRUCTURED,
        syntax: 'ARRAYS_TO_OBJECT( <key_array> , <value_array> )',
        completion: 'ARRAYS_TO_OBJECT(${key_array}, ${value_array})',
        summary:
            'Returns an OBJECT that contains the keys specified by one input ARRAY and the values specified by another input ARRAY.'
    },
    {
        name: 'ARRAY_DISTINCT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_DISTINCT( <array> )',
        completion: 'ARRAY_DISTINCT(${array})',
        summary:
            'Returns a new ARRAY that contains only the distinct elements from the input ARRAY.'
    },
    {
        name: 'ARRAY_REMOVE',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_REMOVE( <array> , <value_of_elements_to_remove> )',
        completion: 'ARRAY_REMOVE(${array}, ${value_of_elements_to_remove})',
        summary:
            'Given a source ARRAY, returns an ARRAY with elements of the specified value removed.'
    },
    {
        name: 'ARRAY_CONSTRUCT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_CONSTRUCT( [ <expr1> ] [ , <expr2> [ , ... ] ] )',
        completion: 'ARRAY_CONSTRUCT(${expr1}, ${expr2})',
        summary: 'Returns an array constructed from zero, one, or more inputs.'
    },
    {
        name: 'IS_OBJECT',
        category: Category.STRUCTURED,
        syntax: 'IS_OBJECT( <variant_expr> )',
        completion: 'IS_OBJECT(${variant_expr})',
        summary: 'Returns TRUE if its VARIANT argument contains an OBJECT value.'
    },
    {
        name: 'ARRAY_INSERT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_INSERT( <array> , <pos> , <new_element> )',
        completion: 'ARRAY_INSERT(${array}, ${pos}, ${new_element})',
        summary:
            'Returns an array containing all elements from the source array as well as the new element.'
    },
    {
        name: 'ARRAY_COMPACT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_COMPACT( <array1> )',
        completion: 'ARRAY_COMPACT(${array1})',
        summary:
            'Returns a compacted array with missing and null values removed, effectively converting sparse arrays into dense arrays.'
    },
    {
        name: 'ARRAY_MIN',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_MIN( <array> )',
        completion: 'ARRAY_MIN(${array})',
        summary:
            'Given an input ARRAY, returns the element with the lowest value that is not a SQL NULL.'
    },
    {
        name: 'GET_PATH',
        category: Category.STRUCTURED,
        syntax: "GET_PATH( <column_identifier> , '<path_name>' )",
        completion: "GET_PATH(${column_identifier}, '${path_name}')",
        summary: 'Extracts a value from semi-structured data using a path name.'
    },
    {
        name: 'IS_DATE',
        category: Category.STRUCTURED,
        syntax: 'IS_DATE( <variant_expr> )',
        completion: 'IS_DATE(${variant_expr})',
        summary: 'Verifies whether a VARIANT value contains a DATE value.'
    },
    {
        name: 'IS_DOUBLE',
        category: Category.STRUCTURED,
        syntax: 'IS_DOUBLE( <variant_expr> )',
        completion: 'IS_DOUBLE(${variant_expr})IS_REAL(${variant_expr})',
        summary:
            'Returns TRUE if its VARIANT argument contains a floating-point value, fixed-point decimal, or integer.'
    },
    {
        name: 'IS_BOOLEAN',
        category: Category.STRUCTURED,
        syntax: 'IS_BOOLEAN( <variant_expr> )',
        completion: 'IS_BOOLEAN(${variant_expr})',
        summary: 'Returns TRUE if its VARIANT argument contains a Boolean value.'
    },
    {
        name: 'IS_TIME',
        category: Category.STRUCTURED,
        syntax: 'IS_TIME( <variant_expr> )',
        completion: 'IS_TIME(${variant_expr})',
        summary: 'Verifies whether a VARIANT value contains a TIME value.'
    },
    {
        name: 'ARRAYS_OVERLAP',
        category: Category.STRUCTURED,
        syntax: 'ARRAYS_OVERLAP( <array1> , <array2> )',
        completion: 'ARRAYS_OVERLAP(${array1}, ${array2})',
        summary: 'Compares whether two arrays have at least one element in common.'
    },
    {
        name: 'ARRAY_SIZE',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_SIZE( <array> )',
        completion: 'ARRAY_SIZE(${array})',
        summary: 'Returns the size of the input array.'
    },
    {
        name: 'AS_OBJECT',
        category: Category.STRUCTURED,
        syntax: 'AS_OBJECT( <variant_expr> )',
        completion: 'AS_OBJECT(${variant_expr})',
        summary: 'Casts a VARIANT value to an object.'
    },
    {
        name: 'OBJECT_DELETE',
        category: Category.STRUCTURED,
        syntax: 'OBJECT_DELETE( <object>, <key1> [, <key2>, ... ] )',
        completion: 'OBJECT_DELETE(${object}, ${key1})',
        summary:
            'Returns an object containing the contents of the input (i.e.source) object with one or more keys removed.'
    },
    {
        name: 'AS_CHAR',
        category: Category.STRUCTURED,
        syntax: 'AS_CHAR( <variant_expr> )',
        completion: 'AS_CHAR(${variant_expr})',
        summary: 'Casts a VARIANT value to a string.'
    },
    {
        name: 'OBJECT_CONSTRUCT_KEEP_NULL',
        category: Category.STRUCTURED,
        syntax: 'OBJECT_CONSTRUCT_KEEP_NULL( [<key1>, <value1> [, <keyN>, <valueN> ...]] )',
        completion: 'OBJECT_CONSTRUCT_KEEP_NULL( [${key1}, ${value1})',
        summary: 'Returns an OBJECT constructed from the arguments.'
    },
    {
        name: 'IS_DECIMAL',
        category: Category.STRUCTURED,
        syntax: 'IS_DECIMAL( <variant_expr> )',
        completion: 'IS_DECIMAL(${variant_expr})',
        summary:
            'Returns TRUE if its VARIANT argument contains a fixed-point decimal value or integer.'
    },
    {
        name: 'IS_TIMESTAMP_TZ',
        category: Category.STRUCTURED,
        syntax: 'IS_TIMESTAMP_TZ( <variant_expr> )',
        completion: 'IS_TIMESTAMP_TZ(${variant_expr})',
        summary: 'Verifies whether a VARIANT value contains the respective TIMESTAMP value.'
    },
    {
        name: 'MAP_CONTAINS_KEY',
        category: Category.STRUCTURED,
        syntax: 'MAP_CONTAINS_KEY( <key> , <map> )',
        completion: 'MAP_CONTAINS_KEY(${key}, ${map})',
        summary: 'Determines whether the specified MAP contains the specified key.'
    },
    {
        name: 'MAP_PICK',
        category: Category.STRUCTURED,
        syntax: 'MAP_PICK( <map>, <key1> [, <key2>, ... ] )',
        completion: 'MAP_PICK(${map}, ${key1})',
        summary: 'Returns a new MAP containing the specified key-value pairs from an existing MAP.'
    },
    {
        name: 'OBJECT_KEYS',
        category: Category.STRUCTURED,
        syntax: 'OBJECT_KEYS( <object> )',
        completion: 'OBJECT_KEYS(${object})',
        summary:
            'Returns an array containing the list of keys in the top-most level of the input object.'
    },
    {
        name: 'CHECK_XML',
        category: Category.STRUCTURED,
        syntax: 'CHECK_XML( <string_containing_xml> [ , <disable_auto_convert> ] )',
        completion: 'CHECK_XML(${string_containing_xml})',
        summary: 'Checks the validity of an XML document.'
    },
    {
        name: 'AS_DECIMAL',
        category: Category.STRUCTURED,
        syntax: 'AS_DECIMAL( <variant_expr> [ , <precision> [ , <scale> ] ] )',
        completion: 'AS_DECIMAL(${variant_expr})',
        summary:
            'Casts a VARIANT value to a fixed-point decimal (does not match floating-point values), with optional precision and scale.'
    },
    {
        name: 'AS_INTEGER',
        category: Category.STRUCTURED,
        syntax: 'AS_INTEGER( <variant_expr> )',
        completion: 'AS_INTEGER(${variant_expr})',
        summary: 'Casts a VARIANT value to an integer.'
    },
    {
        name: 'MAP_INSERT',
        category: Category.STRUCTURED,
        syntax: 'MAP_INSERT( <map> , <key> , <value> [ , <updateFlag> ] )',
        completion: 'MAP_INSERT(${map}, ${key}, ${value})',
        summary:
            'Returns a new MAP consisting of the input MAP with a new key-value pair inserted (an existing key updated with a new value).'
    },
    {
        name: 'AS_BINARY',
        category: Category.STRUCTURED,
        syntax: 'AS_BINARY( <variant_expr> )',
        completion: 'AS_BINARY(${variant_expr})',
        summary: 'Casts a VARIANT value to a binary string.'
    },
    {
        name: 'JSON_EXTRACT_PATH_TEXT',
        category: Category.STRUCTURED,
        syntax: "JSON_EXTRACT_PATH_TEXT( <column_identifier> , '<path_name>' )",
        completion: "JSON_EXTRACT_PATH_TEXT(${column_identifier}, '${path_name}')",
        summary:
            'Parses the first argument as a JSON string and returns the value of the element pointed to by the path in the second argument.'
    },
    {
        name: 'OBJECT_PICK',
        category: Category.STRUCTURED,
        syntax: 'OBJECT_PICK( <object>, <key1> [, <key2>, ... ] )',
        completion: 'OBJECT_PICK(${object}, ${key1})',
        summary:
            'Returns a new OBJECT containing some of the key-value pairs from an existing object.'
    },
    {
        name: 'ARRAY_REMOVE_AT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_REMOVE_AT( <array> , <position> )',
        completion: 'ARRAY_REMOVE_AT(${array}, ${position})',
        summary:
            'Given a source ARRAY, returns an ARRAY with the element at the specified position removed.'
    },
    {
        name: 'IS_ARRAY',
        category: Category.STRUCTURED,
        syntax: 'IS_ARRAY( <variant_expr> )',
        completion: 'IS_ARRAY(${variant_expr})',
        summary: 'Returns TRUE if its VARIANT argument contains an ARRAY value.'
    },
    {
        name: 'ARRAY_GENERATE_RANGE',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_GENERATE_RANGE( <start> , <stop> [ , <step> ] )',
        completion: 'ARRAY_GENERATE_RANGE(${start}, ${stop})',
        summary: 'Returns an ARRAY of integer values within a specified range (e.g. [2, 3, 4]).'
    },
    {
        name: 'CHECK_JSON',
        category: Category.STRUCTURED,
        syntax: 'CHECK_JSON( <string_or_variant_expr> )',
        completion: 'CHECK_JSON(${string_or_variant_expr})',
        summary: 'Checks the validity of a JSON document.'
    },
    {
        name: 'IS_INTEGER',
        category: Category.STRUCTURED,
        syntax: 'IS_INTEGER( <variant_expr> )',
        completion: 'IS_INTEGER(${variant_expr})',
        summary: 'Returns TRUE if its VARIANT argument contains an integer value.'
    },
    {
        name: 'ARRAY_CAT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_CAT( <array1> , <array2> )',
        completion: 'ARRAY_CAT(${array1}, ${array2})',
        summary: 'Returns a concatenation of two arrays.'
    },
    {
        name: 'ARRAY_POSITION',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_POSITION( <variant_expr> , <array> )',
        completion: 'ARRAY_POSITION(${variant_expr}, ${array})',
        summary: 'Returns the index of the first occurrence of an element in an array.'
    },
    {
        name: 'IS_CHAR',
        category: Category.STRUCTURED,
        syntax: 'IS_CHAR( <variant_expr> )',
        completion: 'IS_CHAR(${variant_expr})',
        summary: 'Returns TRUE if its VARIANT argument contains a string value.'
    },
    {
        name: 'AS_TIME',
        category: Category.STRUCTURED,
        syntax: 'AS_TIME( <variant_expr> )',
        completion: 'AS_TIME(${variant_expr})',
        summary: 'Casts a VARIANT value to a time value.'
    },
    {
        name: 'AS_ARRAY',
        category: Category.STRUCTURED,
        syntax: 'AS_ARRAY( <variant_expr> )',
        completion: 'AS_ARRAY(${variant_expr})',
        summary: 'Casts a VARIANT value to an array.'
    },
    {
        name: 'OBJECT_CONSTRUCT',
        category: Category.STRUCTURED,
        syntax: 'OBJECT_CONSTRUCT( [<key1>, <value1> [, <keyN>, <valueN> ...]] )',
        completion: 'OBJECT_CONSTRUCT( [${key1}, ${value1})',
        summary: 'Returns an OBJECT constructed from the arguments.'
    },
    {
        name: 'ARRAY_FLATTEN',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_FLATTEN( <array> )',
        completion: 'ARRAY_FLATTEN(${array})',
        summary: 'Flattens an ARRAY of ARRAYs into a single ARRAY.'
    },
    {
        name: 'OBJECT_INSERT',
        category: Category.STRUCTURED,
        syntax: 'OBJECT_INSERT( <object> , <key> , <value> [ , <updateFlag> ] )',
        completion: 'OBJECT_INSERT(${object}, ${key}, ${value})',
        summary:
            'Returns an object consisting of the input object with a new key-value pair inserted (or an existing key updated with a new value).'
    },
    {
        name: 'PARSE_XML',
        category: Category.STRUCTURED,
        syntax: 'PARSE_XML( <string_containing_xml> [ , <disable_auto_convert> ] )',
        completion: 'PARSE_XML(${string_containing_xml})',
        summary: 'Interprets an input string as an XML document, producing an OBJECT value.'
    },
    {
        name: 'GET',
        category: Category.STRUCTURED,
        syntax: 'GET( <array> , <index> )',
        completion: 'GET(${array}, ${index})',
        summary:
            'Extracts a value from an ARRAY or an OBJECT (or a VARIANT that contains an ARRAY or OBJECT).'
    },
    {
        name: 'XMLGET',
        category: Category.STRUCTURED,
        syntax: 'XMLGET( <expression> , <tag_name> [ , <instance_number> ] )',
        completion: 'XMLGET(${expression}, ${tag_name})',
        summary:
            'Extracts an XML element object (often referred to as simply a tag) from the content of the outer XML element based on the name and instance number of the specified tag.'
    },
    {
        name: 'ARRAY_SORT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_SORT( <array> [ , <sort_ascending> [ , <nulls_first> ] ] )',
        completion: 'ARRAY_SORT(${array})',
        summary:
            'Returns an ARRAY that contains the elements of the input ARRAY sorted in ascending or descending order.'
    },
    {
        name: 'ARRAY_EXCEPT',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_EXCEPT( <source_array> , <array_of_elements_to_exclude> )',
        completion: 'ARRAY_EXCEPT(${source_array}, ${array_of_elements_to_exclude})',
        summary:
            'Returns a new ARRAY that contains the elements from one input ARRAY that are not in another input ARRAY.'
    },
    {
        name: 'PARSE_JSON',
        category: Category.STRUCTURED,
        syntax: 'PARSE_JSON( <expr> )',
        completion: 'PARSE_JSON(${expr})',
        summary: 'Interprets an input string as a JSON document, producing a VARIANT value.'
    },
    {
        name: 'ARRAY_SLICE',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_SLICE( <array> , <from> , <to> )',
        completion: 'ARRAY_SLICE(${array}, ${from}, ${to})',
        summary:
            'Returns an array constructed from a specified subset of elements of the input array.'
    },
    {
        name: 'ARRAY_INTERSECTION',
        category: Category.STRUCTURED,
        syntax: 'ARRAY_INTERSECTION( <array1> , <array2> )',
        completion: 'ARRAY_INTERSECTION(${array1}, ${array2})',
        summary: 'Returns an array that contains the matching elements in the two input arrays.'
    },
    {
        name: 'TYPEOF',
        category: Category.STRUCTURED,
        syntax: 'TYPEOF( <expr> )',
        completion: 'TYPEOF(${expr})',
        summary: 'Returns the type of a value stored in a VARIANT column.'
    }
];

export const sqlFunctions = [
    {
        name: 'ANY_VALUE',
        category: Category.AGGREGATION,
        syntax: 'ANY_VALUE(<expr>)',
        completion: 'ANY_VALUE(${expr})',
        summary: 'Returns the first value of a column.'
    },
    {
        name: 'APPROXIMATE_COUNT_DISTINCT',
        category: Category.AGGREGATION,
        syntax: 'APPROXIMATE_COUNT_DISTINCT(<expr>)',
        completion: 'APPROXIMATE_COUNT_DISTINCT(${expr})',
        summary: 'Return an approximate distinct row count.'
    },
    {
        name: 'APPROXIMATE_JACCARD_INDEX',
        category: Category.AGGREGATION,
        syntax: 'APPROXIMATE_JACCARD_INDEX(<variant_expr>)',
        completion: 'APPROXIMATE_JACCARD_INDEX(${expr})',
        summary: 'Similarity approximation of inputs.'
    },
    {
        name: 'APPROXIMATE_SIMILARITY',
        category: Category.AGGREGATION,
        syntax: 'APPROXIMATE_SIMILARITY(<variant_expr>)',
        completion: 'APPROXIMATE_SIMILARITY(${expr})',
        summary: 'Similarity approximation of inputs.'
    },
    {
        name: 'APPROX_COUNT_DISTINCT',
        category: Category.AGGREGATION,
        syntax: 'APPROX_COUNT_DISTINCT(<expr>)',
        completion: 'APPROX_COUNT_DISTINCT(${expr})',
        summary: 'Return an approximate distinct row count.'
    },
    {
        name: 'APPROX_PERCENTILE',
        category: Category.AGGREGATION,
        syntax: 'APPROX_PERCENTILE(<float_expr>, <percentile>)',
        completion: 'APPROX_PERCENTILE(${expr}, ${percentile})',
        summary: 'Return a percentile approximation of inputs.'
    },
    {
        name: 'APPROX_PERCENTILE_ACCUMULATE',
        category: Category.AGGREGATION,
        syntax: 'APPROX_PERCENTILE_ACCUMULATE(<expr>)',
        completion: 'APPROX_PERCENTILE_ACCUMULATE(${expr})',
        summary: 'A tdigest state in JSON format.'
    },
    {
        name: 'APPROX_PERCENTILE_COMBINE',
        category: Category.AGGREGATION,
        syntax: 'APPROX_PERCENTILE_COMBINE(<state>)',
        completion: 'APPROX_PERCENTILE_COMBINE(${state})',
        summary: 'A tdigest state in JSON format.'
    },
    {
        name: 'APPROX_TOP_K',
        category: Category.AGGREGATION,
        syntax: 'APPROX_TOP_K(<expr>)',
        completion: 'APPROX_TOP_K(${expr})',
        summary: 'Array containing top k items and their frequencies.'
    },
    {
        name: 'APPROX_TOP_K_ACCUMULATE',
        category: Category.AGGREGATION,
        syntax: 'APPROX_TOP_K_ACCUMULATE(<expr>)',
        completion: 'APPROX_TOP_K_ACCUMULATE(${expr})',
        summary: 'Returns the Space-Saving summary at the end of aggregation.'
    },
    {
        name: 'APPROX_TOP_K_COMBINE',
        category: Category.AGGREGATION,
        syntax: 'APPROX_TOP_K_COMBINE(<state>)',
        completion: 'APPROX_TOP_K_COMBINE(${state})',
        summary: 'Combines (merges) input states into a single output state.'
    },
    {
        name: 'ARRAYAGG',
        category: Category.AGGREGATION,
        syntax: 'ARRAYAGG(<expr>)',
        completion: 'ARRAYAGG(${expr})',
        summary: 'Returns the input values, pivoted into an ARRAY.'
    },
    {
        name: 'ARRAY_AGG',
        category: Category.AGGREGATION,
        syntax: 'ARRAY_AGG(<expr>)',
        completion: 'ARRAY_AGG(${expr})',
        summary: 'Returns the input values, pivoted into an ARRAY.'
    },
    {
        name: 'ARRAY_UNION_AGG',
        category: Category.AGGREGATION,
        syntax: 'ARRAY_UNION_AGG(<array_expr>)',
        completion: 'ARRAY_UNION_AGG(${array_expr})',
        summary:
            'Returns an ARRAY that contains the union of the distinct values from the input ARRAYs in a column.'
    },
    {
        name: 'ARRAY_UNIQUE_AGG',
        category: Category.AGGREGATION,
        syntax: 'ARRAY_UNIQUE_AGG(<expr>)',
        completion: 'ARRAY_UNIQUE_AGG(${expr})',
        summary:
            'Returns an ARRAY that contains all of the distinct values from the specified column.'
    },
    {
        name: 'AVG',
        category: Category.AGGREGATION,
        syntax: 'AVG(<expr>)',
        completion: 'AVG(${expr})',
        summary: 'Returns the average of the values in a group. Null values are ignored.'
    },
    {
        name: 'BITAND_AGG',
        category: Category.AGGREGATION,
        syntax: 'BITAND_AGG(<expr>)',
        completion: 'BITAND_AGG(${expr})',
        summary: 'Returns the bitwise AND of a column. Null values are ignored.'
    },
    {
        name: 'BITOR_AGG',
        category: Category.AGGREGATION,
        syntax: 'BITOR_AGG(<expr>)',
        completion: 'BITOR_AGG(${expr})',
        summary: 'Returns the bitwise OR of a column. Null values are ignored.'
    },
    {
        name: 'BITXOR_AGG',
        category: Category.AGGREGATION,
        syntax: 'BITXOR_AGG(<expr>)',
        completion: 'BITXOR_AGG(${expr})',
        summary: 'Returns the bitwise XOR of a column. Null values are ignored.'
    },
    {
        name: 'BOOLAND_AGG',
        category: Category.AGGREGATION,
        syntax: 'BOOLAND_AGG(<expr>)',
        completion: 'BOOLAND_AGG(${expr})',
        summary: 'Returns the logical (boolean) AND of a column. Null values are ignored.'
    },
    {
        name: 'BOOLOR_AGG',
        category: Category.AGGREGATION,
        syntax: 'BOOLOR_AGG(<expr>)',
        completion: 'BOOLOR_AGG(${expr})',
        summary: 'Returns the logical (boolean) OR of a column. Null values are ignored.'
    },
    {
        name: 'BOOLXOR_AGG',
        category: Category.AGGREGATION,
        syntax: 'BOOLXOR_AGG(<expr>)',
        completion: 'BOOLXOR_AGG(${expr})',
        summary: 'Returns the logical (boolean) XOR of a column. Null values are ignored.'
    },
    {
        name: 'CORR',
        category: Category.AGGREGATION,
        syntax: 'CORR(<y>, <x>)',
        completion: 'CORR(${y}, ${x})',
        summary: 'Returns the correlation coefficient for non-null pairs in a group.'
    },
    {
        name: 'COUNT',
        category: Category.AGGREGATION,
        syntax: 'COUNT(<expr>)',
        completion: 'COUNT(${expr})',
        summary: 'Return number of non-null values of a given expression.'
    },
    {
        name: 'COUNT(*)',
        category: Category.AGGREGATION,
        syntax: 'COUNT(*)',
        completion: 'COUNT(*)',
        summary: 'Returns the total number of rows.'
    },
    {
        name: 'COUNT_IF',
        category: Category.AGGREGATION,
        syntax: 'COUNT_IF(<bool_expr>)',
        completion: 'COUNT_IF(${expr})',
        summary: 'Returns a conditional count.'
    },
    {
        name: 'COVAR_POP',
        category: Category.AGGREGATION,
        syntax: 'COVAR_POP(<y>, <x>)',
        completion: 'COVAR_POP(${y}, ${x})',
        summary: 'Returns the population covariance for non-null pairs in a group.'
    },
    {
        name: 'COVAR_SAMP',
        category: Category.AGGREGATION,
        syntax: 'COVAR_SAMP(<y>, <x>)',
        completion: 'COVAR_SAMP(${y}, ${x})',
        summary: 'Returns the sample covariance for non-null pairs in a group.'
    },
    {
        name: 'CUME_DIST',
        category: Category.WINDOWING,
        syntax: 'CUME_DIST()',
        completion: 'CUME_DIST()',
        summary:
            'Finds the cumulative distribution of a value with regard to other values within the same window partition.'
    },
    {
        name: 'DENSE_RANK',
        category: Category.WINDOWING,
        syntax: 'DENSE_RANK()',
        completion: 'DENSE_RANK()',
        summary:
            'Returns the rank of a value within a group of values, without gaps in the ranks. The rank value starts at 1 and continues up sequentially. If two values are the same, they have the same rank.'
    },
    {
        name: 'FIRST_VALUE',
        category: Category.WINDOWING,
        syntax: 'FIRST_VALUE(<expr>)',
        completion: 'FIRST_VALUE(${expr})',
        summary: 'Returns the first value within an ordered group of values.'
    },
    {
        name: 'HASH_AGG',
        category: Category.AGGREGATION,
        syntax: 'HASH_AGG(<expr>)',
        completion: 'HASH_AGG(${expr})',
        summary:
            'Returns an aggregate signed 64-bit hash value over the (unordered) set of input rows.'
    },
    {
        name: 'HLL',
        category: Category.AGGREGATION,
        syntax: 'HLL(<expr>)',
        completion: 'HLL(${expr})',
        summary: 'Return an approximate distinct row count.'
    },
    {
        name: 'HLL_ACCUMULATE',
        category: Category.AGGREGATION,
        syntax: 'HLL_ACCUMULATE(<expr>)',
        completion: 'HLL_ACCUMULATE(${expr})',
        summary: 'Returns the accumulated HyperLogLog state.'
    },
    {
        name: 'HLL_COMBINE',
        category: Category.AGGREGATION,
        syntax: 'HLL_COMBINE(<state>)',
        completion: 'HLL_COMBINE(${state})',
        summary: 'Return the combined HyperLogLog state.'
    },
    {
        name: 'KURTOSIS',
        category: Category.AGGREGATION,
        syntax: 'KURTOSIS(<expr>)',
        completion: 'KURTOSIS(${expr})',
        summary: 'Returns the sample kurtosis of the values in a group. Null values are ignored.'
    },
    {
        name: 'LAG',
        category: Category.WINDOWING,
        syntax: 'LAG(<expr> [, <offset>, <default>])',
        completion: 'LAG(${expr})',
        summary: 'Accesses data in a previous row in the same result set.'
    },
    {
        name: 'LAST_VALUE',
        category: Category.WINDOWING,
        syntax: 'LAST_VALUE(<expr>)',
        completion: 'LAST_VALUE(${expr})',
        summary: 'Returns the last value within an ordered group of values.'
    },
    {
        name: 'LEAD',
        category: Category.WINDOWING,
        syntax: 'LEAD(<expr> [, <offset>, <default>])',
        completion: 'LEAD(${expr})',
        summary: 'Accesses data in a subsequent row in the same result set.'
    },
    {
        name: 'LISTAGG',
        category: Category.AGGREGATION,
        syntax: 'LISTAGG(<expr> [, <delimiter>])',
        completion: 'LISTAGG(${expr}, ${delimiter})',
        summary: 'Returns the concatenated input values, separated by the delimiter string.'
    },
    {
        name: 'MAX',
        category: Category.AGGREGATION,
        syntax: 'MAX(<expr>)',
        completion: 'MAX(${expr})',
        summary: 'Returns the maximum of a column.'
    },
    {
        name: 'MAX_BY',
        category: Category.AGGREGATION,
        syntax: 'MAX_BY( <col_to_return>, <col_containing_maximum> [ , <maximum_number_of_values_to_return> ] )',
        completion: 'MAX_BY(${col_to_return}, ${col_containing_maximum})',
        summary:
            'Finds the row(s) containing the maximum value for a column and returns the value of another column in that row.'
    },
    {
        name: 'MEDIAN',
        category: Category.AGGREGATION,
        syntax: 'MEDIAN(<expr>)',
        completion: 'MEDIAN(${expr})',
        summary: 'Return the median of a set of values.'
    },
    {
        name: 'MIN',
        category: Category.AGGREGATION,
        syntax: 'MIN(<expr>)',
        completion: 'MIN(${expr})',
        summary: 'Returns the minimum of a column.'
    },
    {
        name: 'MIN_BY',
        category: Category.AGGREGATION,
        syntax: 'MIN_BY( <col_to_return>, <col_containing_maximum> [ , <maximum_number_of_values_to_return> ] )',
        completion: 'MIN_BY(${col_to_return}, ${col_containing_maximum})',
        summary:
            'Finds the row(s) containing the minimum value for a column and returns the value of another column in that row.'
    },
    {
        name: 'MINHASH',
        category: Category.AGGREGATION,
        syntax: 'MINHASH(<k>, <expr>)',
        completion: 'MINHASH(${k}, ${expr})',
        summary: 'Minhash state including an array of k (first input arg) minimum hash values.'
    },
    {
        name: 'MINHASH_COMBINE',
        category: Category.AGGREGATION,
        syntax: 'MINHASH_COMBINE(<state>)',
        completion: 'MINHASH_COMBINE(${state})',
        summary: 'Combined result of two (or more) minhash states.'
    },
    {
        name: 'MODE',
        category: Category.AGGREGATION,
        syntax: 'MODE(<expr>)',
        completion: 'MODE(${expr})',
        summary: 'Returns the most frequent item of a column.'
    },
    {
        name: 'NTH_VALUE',
        category: Category.WINDOWING,
        syntax: 'NTH_VALUE(<expr>, <n>)',
        completion: 'NTH_VALUE(${expr})',
        summary: 'Returns the nth value (up to 1000) within an ordered group of values.'
    },
    {
        name: 'NTILE',
        category: Category.WINDOWING,
        syntax: 'NTILE(<constant_n>)',
        completion: 'NTILE(${constant_n})',
        summary:
            'Divides an ordered data set equally into the number of buckets. Buckets are sequentially numbered starting with 1.'
    },
    {
        name: 'OBJECTAGG',
        category: Category.AGGREGATION,
        syntax: 'OBJECTAGG(<key>, <value>)',
        completion: 'OBJECTAGG(${key}, ${value})',
        summary: 'Aggregate keys and values into an output object.'
    },
    {
        name: 'OBJECT_AGG',
        category: Category.AGGREGATION,
        syntax: 'OBJECT_AGG(<key>, <value>)',
        completion: 'OBJECT_AGG(${key}, ${value})',
        summary: 'Aggregate keys and values into an output object.'
    },
    {
        name: 'PERCENTILE_CONT',
        category: Category.AGGREGATION,
        syntax: 'PERCENTILE_CONT(<percentile>)',
        completion: 'PERCENTILE_CONT(${percentile})',
        summary: 'Returns the value of sort key at specified percentile.'
    },
    {
        name: 'PERCENTILE_DISC',
        category: Category.AGGREGATION,
        syntax: 'PERCENTILE_DISC(<percentile>)',
        completion: 'PERCENTILE_DISC(${percentile})',
        summary: 'Returns the value of sort key at specified percentile.'
    },
    {
        name: 'PERCENT_RANK',
        category: Category.WINDOWING,
        syntax: 'PERCENT_RANK()',
        completion: 'PERCENT_RANK()',
        summary:
            'Returns the relative rank of a value within a group of values, specified as a percentage ranging from 0.0 to 1.0.'
    },
    {
        name: 'RANK',
        category: Category.WINDOWING,
        syntax: 'RANK()',
        completion: 'RANK()',
        summary:
            'Returns the rank of a value within an ordered group of values. The rank value starts at 1 and continues up sequentially. If two values are the same, they have the same rank.'
    },
    {
        name: 'REGR_AVGX',
        category: Category.AGGREGATION,
        syntax: 'REGR_AVGX(<y>, <x>)',
        completion: 'REGR_AVGX(${y}, ${x})',
        summary:
            'Returns the average of the independent variable (2nd parameter) for non-null pairs.'
    },
    {
        name: 'REGR_AVGY',
        category: Category.AGGREGATION,
        syntax: 'REGR_AVGY(<y>, <x>)',
        completion: 'REGR_AVGY(${y}, ${x})',
        summary: 'Returns the average of the dependent variable (1st parameter) for non-null pairs.'
    },
    {
        name: 'REGR_COUNT',
        category: Category.AGGREGATION,
        syntax: 'REGR_COUNT(<y>, <x>)',
        completion: 'REGR_COUNT(${y}, ${x})',
        summary: 'Returns the number of non-null number pairs.'
    },
    {
        name: 'REGR_INTERCEPT',
        category: Category.AGGREGATION,
        syntax: 'REGR_INTERCEPT(<y>, <x>)',
        completion: 'REGR_INTERCEPT(${y}, ${x})',
        summary:
            'Returns the intercept of the univariate linear regression line for non-null pairs.'
    },
    {
        name: 'REGR_R2',
        category: Category.AGGREGATION,
        syntax: 'REGR_R2(<y>, <x>)',
        completion: 'REGR_R2(${y}, ${x})',
        summary: 'Returns the coefficient of determination for non-null pairs.'
    },
    {
        name: 'REGR_SLOPE',
        category: Category.AGGREGATION,
        syntax: 'REGR_SLOPE(<y>, <x>)',
        completion: 'REGR_SLOPE(${y}, ${x})',
        summary: 'Returns the slope of the linear regression line for non-null pairs.'
    },
    {
        name: 'REGR_SXX',
        category: Category.AGGREGATION,
        syntax: 'REGR_SXX(<y>, <x>)',
        completion: 'REGR_SXX(${y}, ${x})',
        summary: 'Returns REGR_COUNT(y, x) * VAR_POP(x) for non-null pairs.'
    },
    {
        name: 'REGR_SXY',
        category: Category.AGGREGATION,
        syntax: 'REGR_SXY(<y>, <x>)',
        completion: 'REGR_SXY(${y}, ${x})',
        summary: 'Returns REGR_COUNT(y, x) * COVAR_POP(y, x) for non-null pairs.'
    },
    {
        name: 'REGR_SYY',
        category: Category.AGGREGATION,
        syntax: 'REGR_SYY(<y>, <x>)',
        completion: 'REGR_SYY(${y}, ${x})',
        summary: 'Returns REGR_COUNT(y, x) * VAR_POP(y) for non-null pairs.'
    },
    {
        name: 'ROW_NUMBER',
        category: Category.WINDOWING,
        syntax: 'ROW_NUMBER()',
        completion: 'ROW_NUMBER()',
        summary:
            'Returns a unique row number for each row within a window partition. The row number starts at 1 and continues up sequentially.'
    },
    {
        name: 'SKEW',
        category: Category.AGGREGATION,
        syntax: 'SKEW(<expr>)',
        completion: 'SKEW(${expr})',
        summary: 'Returns the sample skew of the values in a group. Null values are ignored.'
    },
    {
        name: 'SPLIT_TO_TABLE',
        category: Category.STRING,
        syntax: 'SPLIT_TO_TABLE(<string>, <delimiter>)',
        completion: 'SPLIT_TO_TABLE(${string}, ${delimiter})',
        summary:
            'This table function splits a string (based on a specified delimiter) and flattens the results into rows.'
    },
    {
        name: 'STDDEV',
        category: Category.AGGREGATION,
        syntax: 'STDDEV(<expr>)',
        completion: 'STDDEV(${expr})',
        summary:
            'Returns the sample standard deviation of the values in a group. Null values are ignored.'
    },
    {
        name: 'STDDEV_POP',
        category: Category.AGGREGATION,
        syntax: 'STDDEV_POP(<expr>)',
        completion: 'STDDEV_POP(${expr})',
        summary:
            'Returns the population standard deviation of the values in a group. Null values are ignored.'
    },
    {
        name: 'STDDEV_SAMP',
        category: Category.AGGREGATION,
        syntax: 'STDDEV_SAMP(<expr>)',
        completion: 'STDDEV_SAMP(${expr})',
        summary:
            'Returns the sample standard deviation of the values in a group. Null values are ignored.'
    },
    {
        name: 'STRTOK_SPLIT_TO_TABLE',
        category: Category.STRING,
        syntax: 'STRTOK_SPLIT_TO_TABLE(<string> [,<delimiter_list>])',
        completion: 'STRTOK_SPLIT_TO_TABLE(${string}, ${delimiter})',
        summary:
            'Tokenizes a string with the given set of delimiters and flattens the results into rows.'
    },
    {
        name: 'SUM',
        category: Category.AGGREGATION,
        syntax: 'SUM(<expr>)',
        completion: 'SUM(${expr})',
        summary: 'Returns a sum of a column.'
    },
    {
        name: 'VARIANCE',
        category: Category.AGGREGATION,
        syntax: 'VARIANCE(<expr>)',
        completion: 'VARIANCE(${expr})',
        summary: 'Returns the sample variance of the values in a group. Null values are ignored.'
    },
    {
        name: 'VARIANCE_POP',
        category: Category.AGGREGATION,
        syntax: 'VARIANCE_POP(<expr>)',
        completion: 'VARIANCE_POP(${expr})',
        summary:
            'Returns the population variance of the values in a group. Null values are ignored.'
    },
    {
        name: 'VARIANCE_SAMP',
        category: Category.AGGREGATION,
        syntax: 'VARIANCE_SAMP(<expr>)',
        completion: 'VARIANCE_SAMP(${expr})',
        summary: 'Returns the sample variance of the values in a group. Null values are ignored.'
    },
    {
        name: 'VAR_POP',
        category: Category.AGGREGATION,
        syntax: 'VAR_POP(<expr>)',
        completion: 'VAR_POP(${expr})',
        summary:
            'Returns the population variance of the values in a group. Null values are ignored.'
    },
    {
        name: 'VAR_SAMP',
        category: Category.AGGREGATION,
        syntax: 'VAR_SAMP(<expr>)',
        completion: 'VAR_SAMP(${expr})',
        summary: 'Returns the sample variance of the values in a group. Null values are ignored.'
    }
];
